import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../api/axios';
import useAuthStore from '../store/authStore';

const QrOrdersContext = createContext(null);

const playQrChime = () => {
    try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const notes = [880, 1108]; // A5, C#6 — pleasant ding-dong
        notes.forEach((freq, i) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.type = 'sine';
            osc.frequency.value = freq;
            const start = ctx.currentTime + i * 0.18;
            gain.gain.setValueAtTime(0, start);
            gain.gain.linearRampToValueAtTime(0.35, start + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.001, start + 0.55);
            osc.start(start);
            osc.stop(start + 0.6);
        });
    } catch {
        // AudioContext blocked (user hasn't interacted yet) — silently ignore
    }
};

export function QrOrdersProvider({ children }) {
    const { user, token } = useAuthStore();
    const [qrRequests, setQrRequests] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const lastSeenQrIdRef = useRef(
        parseInt(localStorage.getItem('fiscal_qr_last_id') || '0', 10)
    );

    // Only poll for roles that handle orders
    const shouldPoll = Boolean(token && user &&
        ['admin', 'superadmin', 'kassir', 'waiter'].includes(user?.role));

    const loadQrRequests = useCallback(async () => {
        if (!token || !user) return;
        try {
            const res = await api.get('/qr-orders');
            const incoming = res.data?.data || [];
            setQrRequests(incoming);
            if (incoming.length > 0) {
                const maxId = Math.max(...incoming.map(o => Number(o.id) || 0));
                if (maxId > lastSeenQrIdRef.current) {
                    playQrChime();
                    lastSeenQrIdRef.current = maxId;
                    localStorage.setItem('fiscal_qr_last_id', String(maxId));
                }
            }
        } catch {
            // silently ignore — QR feature may not be enabled
        }
    }, [token, user]);

    useEffect(() => {
        if (!shouldPoll) return;
        loadQrRequests();
        const interval = setInterval(loadQrRequests, 8000);
        return () => clearInterval(interval);
    }, [shouldPoll, loadQrRequests]);

    return (
        <QrOrdersContext.Provider value={{
            qrRequests,
            qrCount: qrRequests.length,
            modalOpen,
            openModal: () => setModalOpen(true),
            closeModal: () => setModalOpen(false),
            loadQrRequests,
        }}>
            {children}
        </QrOrdersContext.Provider>
    );
}

export function useQrOrders() {
    return useContext(QrOrdersContext);
}
